import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './styles.css';
import {
  sxSubtitle,
  sxTextField,
  sxPagination,
  sxAdvertise,
  sxLabel
} from './sxProps';
import { getHistorico } from '../../../services/BeneficiarioService';
import { buscarGuiaMedico, buscarGuiaMedicoComAgendamento } from '../../../services/SaudeOnlineService';
import { EventoHistorico } from '../../../interfaces/entities/EventoHistorico';
import { CircularProgress, Divider, InputAdornment, Pagination, TextField, Typography } from '@mui/material';
import { faCalendarDay, faFilter } from '@fortawesome/pro-regular-svg-icons';
import FilterSelect from '../../../components/FilterSelect';
import CustomTimeline from './CustomTimeline';
import { FilterInterface } from '../../../interfaces/entities/FilterInterface';
import moment from 'moment';
import ContatoModal from './ContatoModal';
import { Doctor } from '../../../interfaces/entities/Doctor';
import { useNavigate } from 'react-router-dom';
import LoadingApp from '../../../components/LoadingApp';
import ModalFeedBack from '../../../components/ModalFeedBack';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import { scrollTo } from '../../../utils/ScrollUtil';
import dayjs from 'dayjs';
import CalendarModal from './CalendarModal';
import { dateMask } from '../../../utils/DateMask';
import DefaultTextField from '../../../components/DefaultTextField';
import PageTitle from '../../../components/Accessibility/PageTitle';
import { removerAcentos } from '../../../utils/RemoverAcentos';

const LinhaTempo: React.FC = () => {

  const [eventos, setEventos] = useState<EventoHistorico[]>([]);
  const [isLoadingEventos, setLoadingEventos] = useState<boolean>(true);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [filtrosTipo, setFiltrosTipo] = useState<FilterInterface[]>([]);
  const [pesquisar, setPesquisar] = useState<string>('');
  const [filtroTipoSelected, setFiltroTipoSelected] = useState<string | null>(null);
  const [eraseFiltroTipoSelected, setEraseFiltroTipoSelected] = useState<boolean>(false);
  const [showContatoModal, setShowContatoModal] = useState<boolean>(false);
  const [showModalSemReagendamento, setShowModalSemReagendamento] = useState<boolean>(false);
  const [showModalSemContato, setShowModalSemContato] = useState<boolean>(false);
  const [contatoInfo, setContatoInfo] = useState<Doctor[] | null>(null);
  const [page, setPage] = useState<number>(1);
  const paginationLength = 10;
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [selectingDate, setSelectingDate] = useState<string>('START');

  const [filtroDataMinima, setFiltroDataMinima] = useState<string>('');
  const [dateStartSelected, setDateStartSelected] = useState<string>('');
  const [dateEndSelected, setDateEndSelected] = useState<string>('');

  const timelineRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const accessibility = useAccessibility();

  useEffect(() => {
    setLoadingEventos(true);
    setFiltrosTipo([
      { descricao: 'Atendimento Não Médico', id: 'CONSULNAOMED' },
      { descricao: 'Consulta Médica Presencial', id: 'CONSULMED' },
      { descricao: 'Consulta Telemedicina', id: 'TELECONSUL' },
      { descricao: 'Exame', id: 'EXAME' },
      { descricao: 'Internação', id: 'INTERN' },
      { descricao: 'PA Virtual', id: 'PAVIRTUAL' },
      { descricao: 'Urgência/Emergência', id: 'URGENCIA' },
    ]);
    getHistorico()
      .then(response => {
        setEventos(response.eventos);
        setFiltroDataMinima(response.filtroDataMinima)
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setLoadingEventos(false);
      })
  }, []);

  const handlePesquisarChange = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
    setPage(1);
    setPesquisar(event.target.value);
  };

  const handleContatoSelected = (evento: EventoHistorico) => {
    setLoading(true);
    buscarGuiaMedico({
      nome: evento.nomePrestador,
      tipoAtend: evento.codigo === 'TELECONSUL' ? 'T' : 'P',
      start: 0,
      limit: 1000
    })
      .then((response: Doctor[] | null) => {
        if (!response || !response.length) {
          setShowModalSemContato(true);
        } else {
          setContatoInfo(response);
          setShowContatoModal(true);
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      })
  }

  const handleReagendarClicked = (evento: EventoHistorico) => {
    setLoading(true);
    buscarGuiaMedicoComAgendamento(
      {
        latitude: 0,
        longitude: 0,
        limit: '49',
        nome: evento.nomePrestador,
        start: '0',
        tipoAtend: evento.codigo === 'TELECONSUL' ? 'T' : 'P'
      })
      .then(result => {
        setLoading(false);
        if (result && result.length) {
          navigate('/saudeOnline/agendarConsulta', { state: { doctorInfo: result } });
        } else {
          setShowModalSemReagendamento(true);
        }
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      })

  }
  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    scrollTo('scroll-ref');
    timelineRef?.current?.focus();
  };

  const handleCloseContatoModal = () => {
    setContatoInfo(null);
    setShowContatoModal(false);
  }

  const handleFilterTipoSelected = (filtroSelected: string | null) => {
    setLoadingEventos(true);
    setPage(1);
    setFiltroTipoSelected(filtroSelected);

    setTimeout(() => {
      setLoadingEventos(false);
    }, 1000);
  }

  const filteredEventos = eventos
    .filter(evento => {
      if (!pesquisar) return true;
      const pesquisa = removerAcentos(pesquisar).toLowerCase().trim().split(' ');
      const eventoInfo = removerAcentos(`${evento.nomePrestador} ${evento.descricao} ${evento.especialidade}`).toLowerCase();
      return pesquisa.every(pesquisa => eventoInfo.includes(pesquisa));
    })
    .filter(evento => {
      if (!filtroTipoSelected) return true;
      return evento.codigo === filtroTipoSelected;
    })
    .filter(evento => {
      if (!dateStartSelected || !dateEndSelected) return true;

      const startMoment = dayjs(dateStartSelected, 'DD/MM/YYYY');
      const endMoment = dayjs(dateEndSelected, 'DD/MM/YYYY');
      const eventDate = dayjs(evento.dataFormatada, 'DD/MM/YYYY');
      return eventDate.isAfter(startMoment.subtract(1, 'day')) && eventDate.isBefore(endMoment.add(1, 'day'));
    });

  const isFiltered = pesquisar || filtroTipoSelected || dateStartSelected || dateEndSelected;


  const paginatedEventos = filteredEventos
    .slice(
      (page - 1) * paginationLength,
      page * paginationLength
    );

  const limparFiltros = () => {
    setPage(1);
    setPesquisar('');
    setFiltroTipoSelected(null);
    setEraseFiltroTipoSelected(!eraseFiltroTipoSelected);
    setDateStartSelected('');
    setDateEndSelected('');
  }

  useEffect(() => {
    setPage(1);
  }, [dateEndSelected])

  const noEventsMessage = () => {
    if (eventos.length === 0) {
      return 'Nenhuma consulta, exame ou atendimento encontrado.'
    } else if (dateStartSelected) {
      return 'Não existem atendimentos no período selecionado.'
    } else {
      return 'Não existem atendimentos com o filtro selecionado.'
    }
  }

  const showEventoHoje =
    page === 1 &&
    !!eventos.length &&
    eventos[0].dataFormatada !== moment().format("DD/MM/YYYY") &&
    pesquisar === '' && filtroTipoSelected === null && (!dateStartSelected || !dateEndSelected);

  const handlePickDate = (date: string) => {
    setPage(1);
    setShowCalendarModal(false);

    const selectedDate = dayjs(date, "DD/MM/YYYY");

    if (selectingDate === 'START') {
      if (dateEndSelected && selectedDate.isAfter(dayjs(dateEndSelected, "DD/MM/YYYY"))) {
        setDateEndSelected('');
      }
      setDateStartSelected(date);
    } else if (selectingDate === 'END') {
      setDateEndSelected(date);

    }
  };

  const getSelectedDate = () => {
    return selectingDate === 'START'
      ? dateStartSelected
      : dateEndSelected
  }

  const handleDateStartTyped = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPage(1);
    const newDateStart = dateMask(event.target.value);
    setDateStartSelected(newDateStart);

    const startDate = dayjs(newDateStart, "DD/MM/YYYY");
    const endDate = dayjs(dateEndSelected, "DD/MM/YYYY");

    if (startDate.isAfter(endDate)) {
      setDateEndSelected('');
    }
  };

  const handleDateEndTyped = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPage(1);
    setDateEndSelected(dateMask(event.target.value));
  };

  useEffect(() => {
    if (dateEndSelected.length >= 10) {
      setLoadingEventos(true);
      setTimeout(() => {
        setLoadingEventos(false);
      }, 1000);
    }

  }, [dateEndSelected])

  return (
    <div className="container">
      <div id='scroll-ref' />
      <LoadingApp showLoading={isLoading} />
      <PageTitle text="Linha do tempo" />
      <Typography style={accessibility.textStyleAccessibility} sx={sxSubtitle}>Confira todas as suas consultas, exames e atendimentos realizados no sistema Unimed Campinas.</Typography>

      <Divider style={{ marginBottom: '20px' }} />
      <div className="filter-area-container">
        <div className='filter-items-container'>
          <div className='filter-header'>
            <h1 style={accessibility.textStyleAccessibility} className='filter-title'><FontAwesomeIcon icon={faFilter} />&nbsp;Filtrar resultados</h1>
          </div>

          <div className="filter-row">
            <div className="filter-item">
              <DefaultTextField
                labeled label="Pesquisar por profissional, procedimento ou especialidade"
                handleChange={handlePesquisarChange}
                value={pesquisar}
                placeholder='Exemplos: José (profissional), Consulta (procedimento) ou Cardiologia (especialidade)'
              />
            </div>
            <div className="filter-item">
              <FilterSelect title='Tipo de atendimento' options={filtrosTipo} disabled={false} handleChange={handleFilterTipoSelected} erase={eraseFiltroTipoSelected} />
            </div>
          </div>
          <div className="filter-row-date">
            <div className='filter-item-date'>
              <Typography style={accessibility.textStyleAccessibility} sx={sxLabel}>Data Início</Typography>
              <TextField
                variant="outlined"
                id="dataInicial"
                fullWidth
                onChange={handleDateStartTyped}
                value={dateStartSelected}
                placeholder="dd/mm/aaaa"
                sx={{
                  ...sxTextField,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: accessibility.contrastMode ? 'black' : '',
                    '& fieldset': {
                      borderColor: accessibility.contrastMode ? 'white' : '',
                    },
                  },
                  input: accessibility.alterFontSizeMode ? { fontSize: accessibility.fontSize } : {}
                }}
                inputProps={{ style: { color: accessibility.contrastMode ? 'white' : '' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <FontAwesomeIcon style={accessibility.textStyleAccessibility} tabIndex={0}
                      onClick={() => {
                        setSelectingDate('START')
                        setShowCalendarModal(true)
                      }}
                      className='calendar-icon' icon={faCalendarDay} />
                  </InputAdornment>,
                }}
              />
            </div>
            <div className='filter-item-date'>
              <Typography style={accessibility.textStyleAccessibility} sx={sxLabel}>Data Fim</Typography>
              <TextField
                variant="outlined"
                id="dataFinal"
                fullWidth
                onChange={handleDateEndTyped}
                value={dateEndSelected}
                placeholder={!dateStartSelected ? 'Informe primeiro a data início' : 'Informe a data fim'}
                disabled={!dateStartSelected}
                sx={{
                  ...sxTextField,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: accessibility.contrastMode ? 'black' : '',
                    '& fieldset': {
                      borderColor: accessibility.contrastMode ? 'white' : '',
                    },
                  },
                  input: accessibility.alterFontSizeMode ? { fontSize: accessibility.fontSize } : {}
                }}
                inputProps={{ style: { color: accessibility.contrastMode ? 'white' : '' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <FontAwesomeIcon style={accessibility.textStyleAccessibility} tabIndex={0}
                      onClick={() => {
                        setSelectingDate('END');
                        setShowCalendarModal(true);
                      }} className='calendar-icon' icon={faCalendarDay} />
                  </InputAdornment>,
                }}
              />
            </div>
          </div>
          {
            isFiltered &&
            <div className="filter-row-clear">
              <button style={accessibility.textStyleAccessibility} tabIndex={0} className="clean-filters-button" onClick={() => { limparFiltros() }}>
                <u>Limpar filtros</u>
              </button>
            </div>
          }
        </div>
      </div>

      <Divider style={{ marginBottom: '10px' }} />

      {isLoadingEventos &&
        <div className="circular-progress">
          <CircularProgress />
        </div>
      }

      {
        !isLoadingEventos && !!filteredEventos.length &&
        <div ref={timelineRef} tabIndex={-1}>
          <CustomTimeline
            eventos={paginatedEventos}
            handleContatoSelected={handleContatoSelected}
            handleReagendarClicked={handleReagendarClicked}
            showHoje={showEventoHoje}
          />

          {
            filteredEventos.length > paginationLength &&
            <Pagination
              className='pagination-holder'
              count={Math.ceil(filteredEventos.length / paginationLength)}
              sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
              color='primary'
              page={page}
              onChange={handlePageChange}
            />
          }

        </div>
      }

      {
        !isLoadingEventos && !filteredEventos.length &&
        <Typography style={{ ...{ marginTop: '25px' }, ...accessibility.textStyleAccessibility }} sx={sxAdvertise}>{noEventsMessage()}</Typography>
      }

      <ContatoModal
        open={showContatoModal}
        handleCloseModal={() => { handleCloseContatoModal() }}
        contatos={contatoInfo}
      />

      <ModalFeedBack
        showModal={showModalSemReagendamento}
        handleShowModal={() => { setShowModalSemReagendamento(!showModalSemReagendamento) }}
        icon={'warning'}
        textButton={'Aviso'}
        title={'Atenção'}
        subTitle={'Médico sem data disponível'}
        handleButtonModal={() => { }}
      />

      <ModalFeedBack
        showModal={showModalSemContato}
        handleShowModal={() => { setShowModalSemContato(!showModalSemContato) }}
        icon={'warning'}
        textButton={'Aviso'}
        title={'Atenção'}
        subTitle={'Os dados deste profissional não estão mais disponíveis.'}
        handleButtonModal={() => { }}
      />

      <CalendarModal
        open={showCalendarModal}
        selectedDate={getSelectedDate()}
        handlePickDate={handlePickDate}
        handleCloseModal={() => { setShowCalendarModal(false) }}
        minDate={selectingDate === 'START' ? filtroDataMinima : dateStartSelected}
      />
    </div>
  )
}

export default LinhaTempo